/**
 * Error Navigation Utility
 *
 * Maps form field paths to form steps and provides utilities
 * to navigate users to the step containing the first validation error.
 */
import { FieldErrors } from "react-hook-form";

import { ALL_STEPS, StepId } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";

// =============================================================================
// FIELD PATH TO STEP MAPPING
// =============================================================================

/**
 * Maps field path prefixes to their corresponding step IDs.
 * Order matters - more specific paths should come before generic ones.
 */
const FIELD_PATH_TO_STEP_MAP: Array<{ pathPrefix: string; stepId: StepId }> = [
  { pathPrefix: "attributes.version_name", stepId: "define-version-settings" },
  {
    pathPrefix: "attributes.case_properties",
    stepId: "define-case-properties",
  },
  {
    pathPrefix: "attributes.case_blocks_configs",
    stepId: "configure-case-blocks",
  },
  {
    pathPrefix: "attributes.file_categories",
    stepId: "configure-file-categories",
  },
  { pathPrefix: "attributes.case_tasks", stepId: "define-tasks" },
  { pathPrefix: "attributes.case_actions", stepId: "configure-case-actions" },
  { pathPrefix: "attributes.workflow_rules", stepId: "define-workflow-rules" },
  {
    pathPrefix: "attributes.case_creation_flow_details",
    stepId: "select-decision-flow",
  },
];

/**
 * Get the step ID for a given field path.
 * Returns undefined if no matching step is found.
 */
export const getStepIdForFieldPath = (
  fieldPath: string,
): StepId | undefined => {
  for (const { pathPrefix, stepId } of FIELD_PATH_TO_STEP_MAP) {
    if (fieldPath.startsWith(pathPrefix)) {
      return stepId;
    }
  }
  return undefined;
};

// =============================================================================
// ERROR PATH EXTRACTION
// =============================================================================

/**
 * Recursively extracts all field paths that have errors from a FieldErrors object.
 */
const extractErrorPaths = (
  errors: FieldErrors<CaseTemplateFormValues>,
  basePath = "",
): string[] => {
  const paths: string[] = [];

  // Early return if this is a leaf error (has message property)
  // This can happen when recursing into array items that are leaf errors
  if ("message" in errors && errors.message) {
    if (basePath) {
      paths.push(basePath);
    }
    return paths;
  }

  for (const key in errors) {
    // Skip React Hook Form's special properties that aren't actual form fields
    if (key === "ref" || key === "type" || key === "types" || key === "root") {
      continue;
    }

    const error = errors[key as keyof typeof errors];
    if (!error) continue;

    const currentPath = basePath ? `${basePath}.${key}` : key;

    // Check if this is a leaf error (has a message)
    if (typeof error === "object" && "message" in error && error.message) {
      paths.push(currentPath);
    }

    // Check if this is a nested object or array
    if (typeof error === "object" && !("message" in error)) {
      // Handle array errors
      if (Array.isArray(error)) {
        error.forEach((item, index) => {
          if (item) {
            paths.push(
              ...extractErrorPaths(
                item as FieldErrors<CaseTemplateFormValues>,
                `${currentPath}[${index}]`,
              ),
            );
          }
        });
      } else {
        // Handle nested object errors
        paths.push(
          ...extractErrorPaths(
            error as FieldErrors<CaseTemplateFormValues>,
            currentPath,
          ),
        );
      }
    }
  }

  return paths;
};

// =============================================================================
// MAIN FUNCTIONS
// =============================================================================

/**
 * Get the step ID that contains the first validation error.
 * Returns undefined if no errors or if errors don't map to any step.
 * Steps are checked in the order they are rendered on the page (ALL_STEPS order).
 *
 * @param errors - The FieldErrors object from React Hook Form's formState
 * @returns The StepId of the first step containing an error, or undefined
 */
export const getFirstErrorStep = (
  errors: FieldErrors<CaseTemplateFormValues>,
): StepId | undefined => {
  const stepsWithErrors = getStepsWithErrors(errors);

  if (stepsWithErrors.length === 0) {
    return undefined;
  }

  // Return the first step (in render order) that has errors
  for (const step of ALL_STEPS) {
    if (stepsWithErrors.includes(step.id)) {
      return step.id;
    }
  }

  return undefined;
};

/**
 * Get all steps that have validation errors.
 * Useful for showing error indicators on multiple steps in the sidebar.
 *
 * @param errors - The FieldErrors object from React Hook Form's formState
 * @returns An array of unique StepIds that contain errors
 */
export const getStepsWithErrors = (
  errors: FieldErrors<CaseTemplateFormValues>,
): StepId[] => {
  const errorPaths = extractErrorPaths(errors);
  const stepsWithErrors = new Set<StepId>();

  for (const path of errorPaths) {
    const stepId = getStepIdForFieldPath(path);
    if (stepId) {
      stepsWithErrors.add(stepId);
    }
  }

  return Array.from(stepsWithErrors);
};

/**
 * Check if a specific step has any validation errors.
 *
 * @param errors - The FieldErrors object from React Hook Form's formState
 * @param stepId - The step ID to check
 * @returns True if the step has errors, false otherwise
 */
export const stepHasErrors = (
  errors: FieldErrors<CaseTemplateFormValues>,
  stepId: StepId,
): boolean => {
  const stepsWithErrors = getStepsWithErrors(errors);
  return stepsWithErrors.includes(stepId);
};
